'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { PRODUCT_CAROUSEL_CACHE_KEY, writeProductCarouselCache } from '@/lib/productCarouselCache'

/**
 * Bump when replacing the service worker so legacy workers/cache are cleared once per device.
 * Keep in sync with `public/main.js` (SW_CLIENT_VERSION).
 */
const SW_CLIENT_VERSION = 'gallium-sw-v4'
const STORAGE_KEY = 'gallium-sw-client-version'

function supportsServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
}

async function applyClientVersionGate(): Promise<void> {
  try {
    const applied = localStorage.getItem(STORAGE_KEY)
    if (applied === SW_CLIENT_VERSION) return

    const regs = await navigator.serviceWorker.getRegistrations()
    await Promise.all(regs.map((r) => r.unregister()))

    try {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    } catch {
      /* Cache API may be unavailable */
    }

    try {
      localStorage.setItem(STORAGE_KEY, SW_CLIENT_VERSION)
    } catch {
      /* private mode */
    }
  } catch {
    /* ignore */
  }
}

/**
 * skipWaiting is used in the SW; when a new worker takes control, `controllerchange` fires.
 * Ignore the first event (initial registration), prompt on subsequent (real updates).
 */
function attachControllerChangeListener(): void {
  let seenFirstControllerChange = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!seenFirstControllerChange) {
      seenFirstControllerChange = true
      return
    }

    toast.message('A new version is available.', {
      description: 'Refresh to load the latest Gallium Filters experience.',
      duration: Infinity,
      action: {
        label: 'Refresh',
        onClick: () => window.location.reload(),
      },
      cancel: {
        label: 'Later',
        onClick: () => undefined,
      },
    })
  })
}

/** Safety net if a worker ever stays in `waiting` without skipWaiting. */
function attachWaitingWorkerListener(reg: ServiceWorkerRegistration): void {
  reg.addEventListener('updatefound', () => {
    const nw = reg.installing
    if (!nw) return
    nw.addEventListener('statechange', () => {
      if (nw.state === 'installed' && navigator.serviceWorker.controller) {
        const reload = () => window.location.reload()
        if (
          window.confirm(
            'A new version of the app is ready. Refresh now to update?\n\n(Cancel keeps the current version until you reload manually.)',
          )
        ) {
          reload()
        }
      }
    })
  })
}

async function registerServiceWorker(): Promise<void> {
  await applyClientVersionGate()
  const reg = await navigator.serviceWorker.register('/sw.js', {
    updateViaCache: 'none',
  })
  attachWaitingWorkerListener(reg)

  const pingUpdate = () => void reg.update()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pingUpdate()
  })
  window.addEventListener('focus', pingUpdate)
}

async function warmCarouselDataCache(): Promise<void> {
  try {
    const res = await fetch('/api/products/carousel', { method: 'GET' })
    if (!res.ok) return

    const payload = (await res.json()) as { products?: unknown[] }
    const products = Array.isArray(payload.products) ? payload.products : []
    if (products.length > 0) {
      await writeProductCarouselCache(PRODUCT_CAROUSEL_CACHE_KEY, products)
    }
  } catch {
    // Best effort warmup for offline UX.
  }
}

export default function ServiceWorkerRegister() {
  useEffect(() => {
    if (!supportsServiceWorker()) return

    attachControllerChangeListener()

    const run = async () => {
      try {
        await registerServiceWorker()
        await warmCarouselDataCache()
      } catch (err) {
        console.error('SW registration failed:', err)
      }
    }

    if (document.readyState === 'complete') void run()
    else window.addEventListener('load', () => void run(), { once: true })
  }, [])

  return null
}
